import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useRecordSearch } from '@/store/searchHistoryStore';
import { useSearchOverlayOpen, useSearchQuery, useSetSearchQuery, useCloseSearchOverlay } from '@/store';
import { SearchResultsFeed } from '@/components/features/Search/SearchResultsFeed';
import { SearchEntryState } from '@/components/features/Search/SearchEntryState';
import { SearchCategoryFilter } from '@/components/features/Search/SearchCategoryFilter';

/**
 * Body of the in-header search (ARC-113), paired with {@link HeaderSearch}. The
 * field writes the query to the search-overlay store; this reads it back, so the
 * two stay in sync without prop drilling. Mounted in the app shell in the page
 * content area and shown only while search is open, with **no background of its
 * own** so the page's own gradient shows through (the feed is hidden meanwhile).
 *
 * The category filter reappears under the bar, then by debounced query it shows
 * either live results ({@link SearchResultsFeed}) or the entry state
 * ({@link SearchEntryState}, recent + popular). Search is always event-centric.
 */
export function SearchOverlayBody() {
  const isOpen = useSearchOverlayOpen();
  const query = useSearchQuery();
  const setQuery = useSetSearchQuery();
  const recordSearch = useRecordSearch();
  const close = useCloseSearchOverlay();
  const { pathname } = useLocation();

  const debouncedQuery = useDebouncedValue(query, 250);
  const hasQuery = debouncedQuery.trim().length > 0;

  // Selecting a result/popular/related row navigates to its event — close search
  // on any route change so the overlay never lingers over a different page.
  // (Category-filter changes touch only the query string, not pathname.)
  useEffect(() => {
    if (isOpen) close();
    // Run only when the path changes — `close`/`isOpen` deliberately omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Click-away: a press anywhere outside the search surfaces (the header field
  // and this body) closes search — e.g. tapping the top bar chrome. Rows and
  // filter pills live inside a surface, so they don't trigger it.
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (!target?.closest('[data-search-surface]')) close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isOpen, close]);

  function pickRecent(recent: string) {
    setQuery(recent);
    recordSearch(recent);
  }

  if (!isOpen) return null;

  return (
    <div data-search-surface className="flex flex-1 flex-col pb-[calc(82px+env(safe-area-inset-bottom))] md:pb-0">
      <div className="w-full mx-auto md:px-6 lg:px-8 xl:px-10 xl:max-w-[1100px] 2xl:max-w-[1300px] mt-2">
        <SearchCategoryFilter className="mb-4" />
        {hasQuery ? (
          <SearchResultsFeed query={debouncedQuery} onResultOpen={recordSearch} />
        ) : (
          <SearchEntryState onPickRecent={pickRecent} />
        )}
      </div>
    </div>
  );
}
